import {
  IAgentData,
  EAgentMentionCapability,
  EAgentPermissionSource,
  IAgentPermissionContext,
  IMgmtPermissionItem,
  IDashboardGamePermissionItem,
} from '../types';
import { DATABRAIN_AGENT_ID } from '@/pages/aigc/pages/Agent/config/constant';
import {
  find,
  flatMap,
  get,
  includes,
  isEmpty,
  some,
} from 'lodash-es';

/** 经分 Level 1 角色标识 */
const DASHBOARD_LEVEL_1_ROLE = 'game_level_1';

/**
 * 判断 Agent 是否支持指定唤起能力
 */
export const hasAgentMentionCapability = (
  agent: IAgentData | undefined,
  capability: EAgentMentionCapability,
): boolean => includes(get(agent, 'mentionCapabilities', []), capability);

/**
 * 按 URL template/tab 过滤 Agent 列表
 * - sceneKey 为空，或没有任何 agent 的 visibleScenes 命中时，返回原列表
 * - 有命中时只保留 visibleScenes 包含 sceneKey 的 agent
 */
export const filterAgentsByScene = (
  agents: IAgentData[] | undefined,
  sceneKey: string,
): IAgentData[] => {
  const base = agents || [];
  if (!sceneKey) return base;
  const hasMatch = some(
    base,
    (agent) =>
      !isEmpty(get(agent, 'visibleScenes')) &&
      includes(agent.visibleScenes, sceneKey),
  );
  if (!hasMatch) return base;
  return base.filter((agent) => includes(get(agent, 'visibleScenes'), sceneKey));
};

/** 有知识库勾选时：只保留支持 KNOWLEDGE 的 Agent（排除 DataBrain 默认 Agent） */
export const filterAgentsForKnowledgeSelection = (
  agents: IAgentData[] | undefined,
  hasKnowledgeSelected: boolean,
): IAgentData[] => {
  const base = agents || [];
  if (!hasKnowledgeSelected) return base;
  return base.filter(
    (agent) =>
      hasAgentMentionCapability(agent, EAgentMentionCapability.KNOWLEDGE) &&
      agent.agentId !== DATABRAIN_AGENT_ID,
  );
};

/** 当前 Agent 不在列表内时，切到第一个可用项（在知识库 onChange 回调里调用） */
export const ensureAgentInKnowledgeAgentList = (
  agents: IAgentData[],
  selectedAgentId: string | undefined,
  selectAgent: (agentId: string) => void,
): void => {
  if (isEmpty(agents)) return;
  if (some(agents, (agent) => agent.agentId === selectedAgentId)) return;
  const firstId = get(agents, '[0].agentId');
  if (firstId) selectAgent(firstId);
};

/**
 * 解析 Agent 权限来源；缺省 / 未知 → rainbow
 * @param agent - Agent 配置
 * @returns 权限来源枚举值
 */
export const resolvePermissionSource = (
  agent: IAgentData | undefined,
): EAgentPermissionSource => {
  /** 七彩石配置的 permissionSource */
  const source = get(agent, 'permissionSource');
  if (source === EAgentPermissionSource.DASHBOARD) {
    return EAgentPermissionSource.DASHBOARD;
  }
  if (source === EAgentPermissionSource.MGMT) {
    return EAgentPermissionSource.MGMT;
  }
  return EAgentPermissionSource.RAINBOW;
};

/**
 * 展平 main_game 与其子版本 game_List
 * @param games - userMessage.main_game
 * @returns 扁平游戏列表
 */
export const flattenDashboardGames = (
  games: IDashboardGamePermissionItem[] | undefined,
): IDashboardGamePermissionItem[] =>
  flatMap(games || [], (game) => [
    game,
    ...flattenDashboardGames(get(game, 'game_List')),
  ]);

/**
 * 在 main_game（含 game_List）中按 game_code 查找游戏项
 * @param games - userMessage.main_game
 * @param gameCode - Agent.gameCode
 * @returns 命中的游戏项；未找到返回 undefined
 */
export const findDashboardGameByCode = (
  games: IDashboardGamePermissionItem[] | undefined,
  gameCode?: string,
): IDashboardGamePermissionItem | undefined => {
  if (!gameCode || isEmpty(games)) {
    return undefined;
  }
  return find(flattenDashboardGames(games), { game_code: gameCode });
};

/**
 * 经分 Level 1：role_type=game 且 role_ids/role_id 含 game_level_1
 * @param game - 命中的游戏项
 * @returns 是否具备 Level 1
 */
export const hasDashboardLevel1OnGame = (
  game: IDashboardGamePermissionItem | undefined,
): boolean => {
  if (get(game, 'role_type') !== 'game') {
    return false;
  }
  if (get(game, 'role_id') === DASHBOARD_LEVEL_1_ROLE) {
    return true;
  }
  return includes(get(game, 'role_ids', []), DASHBOARD_LEVEL_1_ROLE);
};

/**
 * 按 permissionSource 判定是否有权访问 Agent
 * @param params.agent - Agent 配置
 * @param params.userOpenId - rainbow 白名单比对
 * @param params.mgmtPermissions - mgmt permissions
 * @param params.dashboardGames - userMessage.main_game
 * @returns 是否有权限
 */
export const hasAgentPermission = ({
  agent,
  userOpenId,
  mgmtPermissions,
  dashboardGames,
}: {
  agent: IAgentData;
  userOpenId?: string;
  mgmtPermissions?: IMgmtPermissionItem[];
  dashboardGames?: IDashboardGamePermissionItem[];
}): boolean => {
  /** 权限来源 */
  const source = resolvePermissionSource(agent);
  if (source === EAgentPermissionSource.MGMT) {
    return some(mgmtPermissions, {
      permission_key: 'agent',
      readable: 1,
    });
  }
  if (source === EAgentPermissionSource.DASHBOARD) {
    const game = findDashboardGameByCode(dashboardGames, get(agent, 'gameCode'));
    return hasDashboardLevel1OnGame(game);
  }
  const whitelistGroups = get(agent, 'whitelistGroups', []);
  if (isEmpty(whitelistGroups)) {
    return true;
  }
  return !!userOpenId && includes(whitelistGroups, userOpenId);
};

/**
 * 已发布且当前用户有权访问
 * @param agent - Agent 配置
 * @param context - openid / mgmt / dashboard 列表
 * @returns 是否可选
 */
export const isAgentAccessible = (
  agent: IAgentData,
  context: IAgentPermissionContext = {},
): boolean => {
  if (get(agent, 'status') !== 'Published') {
    return false;
  }
  return hasAgentPermission({
    agent,
    userOpenId: get(context, 'userOpenId'),
    mgmtPermissions: get(context, 'mgmtPermissions'),
    dashboardGames: get(context, 'dashboardGames'),
  });
};
